import React, { useState, useEffect, useCallback } from 'react';
import { X, CaretRight, Plugs, GearSix, PaperPlaneTilt, CalendarCheck, Robot } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Human-friendly step settings panel.
 * Shown when a user clicks a step in the workflow builder.
 *
 * Translates raw technical fields (provider, capability, execution_mode)
 * into service-business-friendly language.
 *
 * Principle: Everything is configurable. Not everything is visible.
 */

const CAPABILITY_LABELS = {
  receive_email:        { label: 'Receive email',          icon: PaperPlaneTilt, description: 'Triggers when a new email arrives' },
  send_email:           { label: 'Send email',             icon: PaperPlaneTilt, description: 'Sends an email to the customer' },
  reply_email:          { label: 'Reply to email',         icon: PaperPlaneTilt, description: 'Replies to the original email thread' },
  create_event:         { label: 'Create calendar event',  icon: CalendarCheck,  description: 'Creates an event in your connected calendar' },
  generate_reply:       { label: 'Generate reply',         icon: Robot,          description: 'AI writes a professional response' },
  classify_enquiry:     { label: 'Classify enquiry',       icon: Robot,          description: 'AI determines the type and urgency' },
  generate_summary:     { label: 'Generate summary',       icon: Robot,          description: 'AI creates a summary of the interaction' },
  generate_quote:       { label: 'Generate quote',         icon: Robot,          description: 'AI prepares a pricing estimate' },
  check_business_hours: { label: 'Check business hours',   icon: GearSix,        description: 'Checks if current time is within your working hours' },
  create_work_item:     { label: 'Create work item',       icon: GearSix,        description: 'Creates a review item in your inbox' },
};

const PROVIDER_LABELS = {
  gmail:            'Gmail',
  outlook:          'Outlook',
  google_calendar:  'Google Calendar',
  ai:               'AI',
  platform:         'Platform',
};

const StepSettingsPanel = ({ step, isOpen, onClose, onUpdate, connectedApps }) => {
  const [localSettings, setLocalSettings] = useState({});

  useEffect(() => {
    if (step) {
      setLocalSettings({
        review_before_sending: step.execution_mode !== 'auto' || step.settings?.review_before_sending !== false,
        send_automatically: step.execution_mode === 'auto' && !step.settings?.review_before_sending,
        use_business_hours: step.settings?.use_business_hours !== false,
        event_duration_minutes: step.settings?.event_duration_minutes || 60,
      });
    }
  }, [step]);

  const handleToggle = useCallback((field) => {
    setLocalSettings(prev => {
      const updated = { ...prev, [field]: !prev[field] };
      // Sync paired toggles
      if (field === 'send_automatically' && updated.send_automatically) {
        updated.review_before_sending = false;
      }
      if (field === 'review_before_sending' && updated.review_before_sending) {
        updated.send_automatically = false;
      }
      return updated;
    });
  }, []);

  const handleSave = useCallback(() => {
    if (!step || !onUpdate) return;
    const updated = { ...step };
    updated.execution_mode = localSettings.send_automatically ? 'auto' : 'fallback';
    updated.settings = {
      ...(updated.settings || {}),
      review_before_sending: localSettings.review_before_sending,
      use_business_hours: localSettings.use_business_hours,
      event_duration_minutes: localSettings.event_duration_minutes,
    };
    onUpdate(updated);
    onClose();
  }, [step, localSettings, onUpdate, onClose]);

  if (!step) return null;

  const capInfo = CAPABILITY_LABELS[step.capability] || {};
  const CapIcon = capInfo.icon || GearSix;
  const providerLabel = PROVIDER_LABELS[step.provider] || step.provider || '';
  const isAI = step.type === 'ai' || step.provider === 'ai';
  const isAction = step.type === 'action';
  const isCalendar = step.capability === 'create_event';
  const isEmail = ['send_email', 'reply_email'].includes(step.capability);
  const providerConnected = step.provider && connectedApps?.has?.(step.provider);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 24 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-sm rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#111827] overflow-hidden"
          data-testid="step-settings-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.08)]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[rgba(0,229,192,0.08)] flex items-center justify-center">
                <CapIcon size={16} className="text-[#00E5C0]" weight="duotone" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#F0F4F8]" data-testid="step-settings-title">
                  {step.label}
                </h3>
                <p className="text-[10px] text-[#7D8896]">{capInfo.description || 'Step configuration'}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-[rgba(255,255,255,0.04)] flex items-center justify-center text-[#7D8896]"
              data-testid="step-settings-close">
              <X size={14} />
            </button>
          </div>

          {/* Settings body */}
          <div className="p-4 space-y-4">

            {/* App / Provider */}
            {providerLabel && (
              <div data-testid="step-settings-app">
                <label className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider mb-1.5 block">App</label>
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)]">
                  <Plugs size={14} className="text-[#7D8896]" />
                  <span className="text-sm text-[#F0F4F8]">{providerLabel}</span>
                  {isAction && !isAI && (
                    <span className={`ml-auto text-[9px] font-medium px-1.5 py-0.5 rounded ${
                      providerConnected
                        ? 'text-[#00E5C0] bg-[rgba(0,229,192,0.08)]'
                        : 'text-[#F59E0B] bg-[rgba(245,158,11,0.08)]'
                    }`}>
                      {providerConnected ? 'Connected' : 'Not connected'}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Action */}
            {step.capability && (
              <div data-testid="step-settings-action">
                <label className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider mb-1.5 block">Action</label>
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)]">
                  <CaretRight size={12} className="text-[#7D8896]" />
                  <span className="text-sm text-[#F0F4F8]">{capInfo.label || step.capability}</span>
                </div>
              </div>
            )}

            {/* Email settings */}
            {isEmail && (
              <>
                <ToggleRow
                  label="Review before sending"
                  description="You approve every message before it goes out"
                  checked={localSettings.review_before_sending}
                  onChange={() => handleToggle('review_before_sending')}
                  testId="toggle-review-before-sending"
                />
                <ToggleRow
                  label="Send automatically"
                  description="Messages are sent without manual review"
                  checked={localSettings.send_automatically}
                  onChange={() => handleToggle('send_automatically')}
                  testId="toggle-send-automatically"
                />
              </>
            )}

            {/* Calendar settings */}
            {isCalendar && (
              <>
                <ToggleRow
                  label="Use business hours"
                  description="Default event times align with your working hours"
                  checked={localSettings.use_business_hours}
                  onChange={() => handleToggle('use_business_hours')}
                  testId="toggle-use-business-hours"
                />
                <div data-testid="step-settings-duration">
                  <label className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider mb-1.5 block">Default duration</label>
                  <select
                    value={localSettings.event_duration_minutes}
                    onChange={(e) => setLocalSettings(prev => ({ ...prev, event_duration_minutes: parseInt(e.target.value, 10) }))}
                    className="w-full px-3 py-2.5 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] text-sm text-[#F0F4F8] outline-none focus:border-[rgba(0,229,192,0.3)]"
                    data-testid="select-event-duration"
                  >
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                  </select>
                </div>
              </>
            )}

            {/* AI info */}
            {isAI && (
              <div className="px-3 py-2.5 rounded-lg bg-[rgba(139,143,245,0.04)] border border-[rgba(139,143,245,0.08)]" data-testid="step-settings-ai-info">
                <p className="text-xs text-[#A5A8FC]">This step is powered by AI. It uses your business profile and knowledge documents to generate responses.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {(isEmail || isCalendar) && (
            <div className="p-4 border-t border-[rgba(255,255,255,0.08)]">
              <button onClick={handleSave}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-[#161B26] bg-[#00E5C0] hover:bg-[#00CCB0] transition-colors"
                data-testid="step-settings-save">
                Save settings
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* Toggle row sub-component */
const ToggleRow = ({ label, description, checked, onChange, testId }) => (
  <div className="flex items-center justify-between gap-3" data-testid={testId}>
    <div>
      <p className="text-sm text-[#F0F4F8]">{label}</p>
      {description && <p className="text-[10px] text-[#7D8896] mt-0.5">{description}</p>}
    </div>
    <button
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
        checked ? 'bg-[#00E5C0]' : 'bg-[rgba(255,255,255,0.1)]'
      }`}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
        checked ? 'left-[18px]' : 'left-0.5'
      }`} />
    </button>
  </div>
);

export default StepSettingsPanel;
